// Opmaakbalk van de tekst-editors (helpdesk-antwoord, nieuw ticket, kennisbank,
// mailsjablonen): de keuzelijsten en de bewerkingen die de browser niet zelf
// kent. De knoppen zelf staan in App.jsx (OpmaakBalk); hier staat alles wat
// zonder scherm te testen valt.
//
// De editors werken op een contentEditable met document.execCommand. Dat is oud,
// maar in elke browser aanwezig en het enige dat zonder extra bibliotheek werkt.
// Twee dingen doet execCommand niet goed; die staan hier:
//   • "Opmaak verwijderen" (removeFormat) laat <font>-tags, koppen en de
//     lettergrootte soms staan, en doet niets zonder selectie. Hier: alle
//     inline-opmaak weg uit een stuk DOM, zodat alles weer in hetzelfde
//     lettertype en dezelfde kleur staat — de reden waarom de knop bestaat.
//   • Lettergrootte en lettertype geeft de browser als <font size="7"> of
//     <font face="…">. Mailclients kennen dat nog, maar een gekozen puntgrootte
//     moet erin: de <font> wordt een <span style="…">.

// Kleuren voor tekst en markering (Odoo-achtig: een rij grijstinten en een rij
// kleuren). "Standaard" haalt de kleur weer weg.
export const OPMAAK_KLEUREN = [
  '#000000', '#334155', '#64748b', '#94a3b8', '#cbd5e1', '#ffffff',
  '#dc2626', '#ea580c', '#d97706', '#16a34a', '#0d9488', '#2563eb', '#7c3aed', '#db2777',
];
export const OPMAAK_MARKERINGEN = [
  '#fef08a', '#fde68a', '#bbf7d0', '#a5f3fc', '#bfdbfe', '#e9d5ff', '#fbcfe8', '#e2e8f0',
];
// Puntgroottes in px, zoals een mailclient ze toont.
export const OPMAAK_GROOTTES = [10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 36];
// Lettertypes die in elke mailclient bestaan.
export const OPMAAK_LETTERTYPES = [
  { naam: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { naam: 'Verdana', css: 'Verdana, Geneva, sans-serif' },
  { naam: 'Tahoma', css: 'Tahoma, Geneva, sans-serif' },
  { naam: 'Calibri', css: 'Calibri, Candara, Segoe, sans-serif' },
  { naam: 'Georgia', css: 'Georgia, serif' },
  { naam: 'Times New Roman', css: '"Times New Roman", Times, serif' },
  { naam: 'Courier New', css: '"Courier New", Courier, monospace' },
];
// Blokstijlen voor de keuzelijst "Normaal ▾".
export const OPMAAK_BLOKKEN = [
  { tag: 'p', label: 'Normaal' },
  { tag: 'h1', label: 'Kop 1' },
  { tag: 'h2', label: 'Kop 2' },
  { tag: 'h3', label: 'Kop 3' },
  { tag: 'blockquote', label: 'Citaat' },
  { tag: 'pre', label: 'Code' },
];

// Inline-opmaak die bij "Opmaak verwijderen" verdwijnt (de tekst blijft).
const INLINE_OPMAAK = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'FONT', 'MARK', 'SMALL', 'BIG', 'SUB', 'SUP', 'SPAN', 'CODE', 'KBD', 'VAR', 'TT']);
// Blokken die een gewone alinea worden.
const BLOK_NAAR_P = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE']);
// Attributen die opmaak dragen.
const OPMAAK_ATTRIBUTEN = ['style', 'color', 'face', 'size', 'align', 'bgcolor', 'class'];

// Een variabele-blokje van een mailsjabloon ({{…}} als chip, zie MtVisueleEditor)
// is geen opmaak: dat blijft, mét zijn stijl.
const isChip = (el) => el.nodeType === 1 && el.hasAttribute('data-var');

// Vervang een element door zijn kinderen.
const unwrap = (el) => {
  const ouder = el.parentNode; if (!ouder) return;
  while (el.firstChild) ouder.insertBefore(el.firstChild, el);
  ouder.removeChild(el);
};

// Haal alle inline-opmaak uit `root` (het element zelf blijft). Lijsten, links,
// afbeeldingen, alinea's en regeleinden blijven staan; koppen worden alinea's;
// style-attributen gaan overal weg behalve op variabele-blokjes.
export function verwijderOpmaak(root) {
  if (!root) return root;
  // Eerst alle elementen verzamelen: de boom verandert tijdens het unwrappen.
  const alle = Array.from(root.querySelectorAll('*'));
  for (const el of alle) {
    if (!el.parentNode || isChip(el)) continue;
    if (el.closest && el.closest('[data-var]') && el.closest('[data-var]') !== el && root.contains(el.closest('[data-var]'))) continue;
    const naam = el.nodeName.toUpperCase();
    if (INLINE_OPMAAK.has(naam)) { unwrap(el); continue; }
    if (BLOK_NAAR_P.has(naam)) {
      const p = el.ownerDocument.createElement('p');
      while (el.firstChild) p.appendChild(el.firstChild);
      el.parentNode.replaceChild(p, el);
      continue;
    }
    for (const a of OPMAAK_ATTRIBUTEN) if (el.hasAttribute(a)) el.removeAttribute(a);
  }
  // Lege tekstknopen die na het unwrappen tegen elkaar staan samenvoegen, zodat
  // de cursor en de browser er één tekst van zien.
  if (root.normalize) root.normalize();
  return root;
}

// Zelfde bewerking op een HTML-string (voor de HTML-weergave en de tests).
export function verwijderOpmaakHtml(html, doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return String(html || '');
  const el = doc.createElement('div');
  el.innerHTML = String(html || '');
  verwijderOpmaak(el);
  return el.innerHTML;
}

// De oude HTML-groottes 1–7 in px (voor een <font size> uit een geplakte mail).
const FONT_SIZE_PX = { 1: 10, 2: 13, 3: 16, 4: 18, 5: 24, 6: 32, 7: 48 };
// Eén eigenschap uit een style-attribuut halen.
const zonderStijl = (el, eigenschap) => {
  const s = el.getAttribute('style'); if (!s) return;
  const rest = s.split(';').map((x) => x.trim()).filter((x) => x && !x.toLowerCase().startsWith(`${eigenschap}:`));
  if (rest.length) el.setAttribute('style', `${rest.join(';')};`); else el.removeAttribute('style');
};

// De <font>-tags die execCommand maakt omzetten naar <span style="…">.
//   • De balk kiest voor een grootte altijd size="7" en geeft hier de gewenste
//     `px` mee (null = "Standaard": geen grootte meer). Een andere size (uit
//     een geplakte mail) krijgt de klassieke px-waarde.
//   • Voor een lettertype zet de balk face="…" en geeft `lettertype` mee (de
//     css-waarde, of null = "Standaard"). Zonder die optie blijft de face.
//   • color="…" wordt color.
// Wat de nieuwe keuze overschrijft, verdwijnt uit de kinderen: anders houdt een
// kleiner stuk binnen de selectie zijn oude grootte. Een <font> zonder
// resterende stijl verdwijnt.
export function fontNaarSpan(root, { px, lettertype } = {}) {
  if (!root) return root;
  const fonts = Array.from(root.querySelectorAll('font'));
  for (const f of fonts) {
    if (!f.parentNode) continue;
    const span = f.ownerDocument.createElement('span');
    const stijl = [];
    const size = f.getAttribute('size');
    if (size === '7' && px !== undefined) {
      if (px) stijl.push(`font-size:${px}px`);
      for (const kind of f.querySelectorAll('[style]')) zonderStijl(kind, 'font-size');
    } else if (size && FONT_SIZE_PX[size]) stijl.push(`font-size:${FONT_SIZE_PX[size]}px`);
    const face = f.getAttribute('face');
    if (face && lettertype !== undefined) {
      if (lettertype) stijl.push(`font-family:${lettertype}`);
      for (const kind of f.querySelectorAll('[style]')) zonderStijl(kind, 'font-family');
    } else if (face) stijl.push(`font-family:${face}`);
    if (f.getAttribute('color')) stijl.push(`color:${f.getAttribute('color')}`);
    const eigen = f.getAttribute('style');
    if (eigen) stijl.push(eigen.replace(/;\s*$/, ''));
    if (!stijl.length) { unwrap(f); continue; }
    while (f.firstChild) span.appendChild(f.firstChild);
    span.setAttribute('style', `${stijl.join(';')};`);
    f.parentNode.replaceChild(span, f);
  }
  return root;
}

// Welke blokstijl staat op de cursor? (voor de keuzelijst) — de waarde van
// queryCommandValue('formatBlock') verschilt per browser ('p', 'P', 'div', '').
export function blokVanWaarde(v) {
  const s = String(v || '').toLowerCase().replace(/[<>]/g, '');
  return OPMAAK_BLOKKEN.some((b) => b.tag === s) ? s : 'p';
}

// Lettergrootte in px van een element (voor de keuzelijst): de dichtstbijzijnde
// voorouder met een font-size in px, anders leeg.
export function grootteVan(node, root) {
  let el = node && node.nodeType === 3 ? node.parentNode : node;
  while (el && el !== root && el.nodeType === 1) {
    const m = /font-size:\s*(\d+(?:\.\d+)?)px/i.exec(el.getAttribute('style') || '');
    if (m) return Math.round(Number(m[1]));
    el = el.parentNode;
  }
  return '';
}

// Lettertype van een element (voor de keuzelijst): de naam uit OPMAAK_LETTERTYPES
// waarvan de css overeenkomt met de dichtstbijzijnde font-family, anders leeg.
export function lettertypeVan(node, root) {
  let el = node && node.nodeType === 3 ? node.parentNode : node;
  while (el && el !== root && el.nodeType === 1) {
    const m = /font-family:\s*([^;]+)/i.exec(el.getAttribute('style') || '');
    if (m) {
      const eerste = m[1].split(',')[0].replace(/["']/g, '').trim().toLowerCase();
      const t = OPMAAK_LETTERTYPES.find((x) => x.naam.toLowerCase() === eerste);
      return t ? t.naam : '';
    }
    el = el.parentNode;
  }
  return '';
}
